/** @module pioneer */
import {
	BaseComponent,
	BaseController,
	ComponentRef,
	Entity,
	Quaternion,
	SpheroidComponent,
	Vector3
} from '../../internal';

/**
 * @typedef {Object} HasGetGroundPosition
 * @property {(outPosition: Vector3, outHeightDir: Vector3, position: Vector3) => void} getGroundPosition
 */

/**
 * A controller that clamps an entty to the ground.
 */
export class GroundClampController extends BaseController {
	/**
	 * Constructor.
	 * @param {string} type - the type of the controller
	 * @param {string} name - the name of the controller
	 * @param {Entity} entity - the parent entity
	 */
	constructor(type, name, entity) {
		super(type, name, entity);

		/**
		 * The component to clamp to.
		 * @type {ComponentRef<BaseComponent & HasGetGroundPosition>}
		 * @private
		 */
		this._groundComponentRef = new ComponentRef(this.getEntity().getScene());

		/**
		 * The offset in the up direction.
		 * @type {number}
		 * @private
		 */
		this._distanceFromGround = 0;

		/**
		 * The frame-up vector for use in clamping correctly to slanted surfaces.
		 * @type {Vector3}
		 * @private
		 */
		this._up = new Vector3(0, 0, 1);

		// Let the base controller know that this changes the position.
		this.addModifiedState('position');
	}

	/**
	 * Sets the component to clamp to. Defaults to the nearest ancestor spheroid or null.
	 * @param {string} entityName
	 * @param {string} componentType
	 * @param {number} [componentTypeIndex]
	 */
	setGroundComponentRef(entityName, componentType, componentTypeIndex) {
		this.removeDependentState(this._groundComponentRef.getEntityName(), 'orientation');
		this._groundComponentRef.setByType(entityName, componentType, componentTypeIndex);
		this.addDependentState(entityName, 'orientation');
	}

	/**
	 * Gets the distance in the up direction from the ground.
	 * @returns {number}
	 */
	getDistanceFromGround() {
		return this._distanceFromGround;
	}

	/**
	 * Sets the distance in the up direction from the ground. Negative is below the ground. Defaults to 0.
	 * @param {number} distanceFromGround
	 */
	setDistanceFromGround(distanceFromGround) {
		this._distanceFromGround = distanceFromGround;
	}

	/**
	 * Sets the frame-up vector for use in clamping correctly to slanted surfaces.
	 * @param {Vector3} up
	 */
	setUp(up) {
		this._up.copy(up);
	}

	/**
	 * Updates a position for the given time.
	 * @param {Vector3} position
	 * @param {number} time
	 * @override
	 * @internal
	 */
	__updatePositionAtTime(position, time) {
		// Get the entity parent.
		const entityParentNameAtTime = this.getEntity().getParentAtTime(time);
		const entityParentAtTime = this.getEntity().getScene().getEntity(entityParentNameAtTime);
		if (entityParentAtTime === null) {
			return;
		}
		// Get the ground component, or get the nearest ancestor with a spheroid.
		let groundComponent = this._groundComponentRef.get();
		if (groundComponent === null) {
			let parent = this.getEntity().getParent();
			while (parent !== null) {
				groundComponent = /** @type {SpheroidComponent} */(parent.getComponentByType('spheroid'));
				if (groundComponent !== null) {
					break;
				}
				parent = parent.getParent();
			}
			if (groundComponent === null) {
				return;
			}
		}
		const groundComponentEntity = groundComponent.getEntity();
		// Get the position of the entity in the component entity's frame-space.
		const entityPosition = Vector3.pool.get();
		const heightDir = Vector3.pool.get();
		const groundOrientationAtTime = Quaternion.pool.get();
		groundComponentEntity.getOrientationAtTime(groundOrientationAtTime, time);
		entityParentAtTime.getPositionRelativeToEntity(entityPosition, position, groundComponentEntity, time);
		entityPosition.rotateInverse(groundOrientationAtTime, entityPosition);
		// Get the height at the given position.
		if (groundComponent.getGroundPosition !== undefined) {
			groundComponent.getGroundPosition(entityPosition, heightDir, entityPosition);
		}
		if (!entityPosition.isNaN()) {
			const entityUp = Vector3.pool.get();
			const entityOrientation = Quaternion.pool.get();
			this.getEntity().getOrientationAtTime(entityOrientation, time);
			entityUp.rotate(entityOrientation, this._up);
			Quaternion.pool.release(entityOrientation);
			entityUp.rotateInverse(groundOrientationAtTime, entityUp);
			const cosUpAndGroundUp = Math.abs(entityUp.dot(heightDir));
			Vector3.pool.release(entityUp);
			// Add on the up offset.
			entityPosition.addMult(entityPosition, heightDir, Math.min(this._distanceFromGround / cosUpAndGroundUp, this._distanceFromGround + this.getEntity().getExtentsRadius()));
			// Get the position back into the entity-space of the parent.
			entityPosition.rotate(groundOrientationAtTime, entityPosition);
			groundComponentEntity.getPositionRelativeToEntity(position, entityPosition, entityParentAtTime, time);
		}
		else {
			position.copy(Vector3.NaN);
		}
		// Cleanup.
		Vector3.pool.release(entityPosition);
		Vector3.pool.release(heightDir);
		Quaternion.pool.release(groundOrientationAtTime);
	}

	/**
	 * Updates the controller.
	 * @override
	 * @internal
	 */
	__update() {
		if (this.getEntity().getParent() === null) {
			return;
		}
		// Get the ground component, or get the nearest ancestor with a spheroid.
		let groundComponent = this._groundComponentRef.get();
		if (groundComponent === null) {
			let parent = this.getEntity().getParent();
			while (parent !== null) {
				groundComponent = /** @type {SpheroidComponent} */(parent.getComponentByType('spheroid'));
				if (groundComponent !== null) {
					break;
				}
				parent = parent.getParent();
			}
			if (groundComponent === null) {
				return;
			}
		}
		const groundComponentEntity = groundComponent.getEntity();
		// Get the position of the entity in the component entity's frame-space.
		const entityPosition = Vector3.pool.get();
		const heightDir = Vector3.pool.get();
		this.getEntity().getPositionRelativeToEntity(entityPosition, Vector3.Zero, groundComponentEntity);
		entityPosition.rotateInverse(groundComponentEntity.getOrientation(), entityPosition);
		// Get the height at the given position.
		if (groundComponent.getGroundPosition !== undefined) {
			groundComponent.getGroundPosition(entityPosition, heightDir, entityPosition);
		}
		if (!entityPosition.isNaN()) {
			const entityUp = Vector3.pool.get();
			entityUp.rotate(this.getEntity().getOrientation(), this._up);
			entityUp.rotateInverse(groundComponentEntity.getOrientation(), entityUp);
			const cosUpAndGroundUp = Math.abs(entityUp.dot(heightDir));
			Vector3.pool.release(entityUp);
			// Add on the up offset.
			entityPosition.addMult(entityPosition, heightDir, Math.min(this._distanceFromGround / cosUpAndGroundUp, this._distanceFromGround + this.getEntity().getExtentsRadius()));
			// Get the position back into the entity-space of the parent.
			entityPosition.rotate(groundComponentEntity.getOrientation(), entityPosition);
			groundComponentEntity.getPositionRelativeToEntity(entityPosition, entityPosition, this.getEntity().getParent());
		}
		else {
			entityPosition.copy(this.getEntity().getLastPosition());
		}
		// Set the position.
		this.getEntity().setPosition(entityPosition);
		Vector3.pool.release(entityPosition);
		Vector3.pool.release(heightDir);
	}
}
